import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bot, ShieldCheck, ShieldAlert, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtTime = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        });
    } catch { return iso; }
};

/**
 * Admin card on the Reports tab:
 *   1. Toggle the after-hours auto-credit feature on/off.
 *   2. Audit queue of receipts auto-credited overnight — sign off OK or
 *      claw back the credit if anything looks off.
 */
const AfterHoursAutoCreditCard = ({ mode = 'admin' }) => {
    // `mode` — 'admin' (default, full settings + stats) or 'cashier' (leaner:
    // just the audit queue, hits /cashier/... scoped endpoints). Reported
    // 2026-07-26: cashier Winnie had no way to review auto-loaded receipts.
    const isCashier = mode === 'cashier';
    const endpoints = isCashier ? {
        list: `${API}/cashier/square-receipts/auto-credited?audit_only=true`,
        ok: (id) => `${API}/cashier/square-receipts/${id}/audit-ok`,
        claw: (id) => `${API}/cashier/square-receipts/${id}/clawback`,
    } : {
        list: `${API}/admin/square-receipts/auto-credited?audit_only=true`,
        ok: (id) => `${API}/admin/square-receipts/${id}/audit-ok`,
        claw: (id) => `${API}/admin/square-receipts/${id}/clawback`,
    };
    const [settings, setSettings] = useState(null);
    const [rows, setRows] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savingToggle, setSavingToggle] = useState(false);
    const [savingAlways, setSavingAlways] = useState(false);
    const [skewDraft, setSkewDraft] = useState('');
    const [savingSkew, setSavingSkew] = useState(false);
    const [clawbackOf, setClawbackOf] = useState(null);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(null); // id of row being acted on
    // Per-row txn ID confirmation input state. Only used for rows flagged
    // suspicious (duplicate/weak-OCR) — clean rows can OK without it.
    const [txnDraft, setTxnDraft] = useState({}); // { [receiptId]: string }

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            if (isCashier) {
                // Cashiers see only the queue — no settings tuning, no
                // stats endpoint (both are admin-scoped).
                const q = await axios.get(endpoints.list, { withCredentials: true });
                setRows(q.data.rows || []);
            } else {
                const [s, q, st] = await Promise.all([
                    axios.get(`${API}/admin/settings/auto-credit-receipts`, { withCredentials: true }),
                    axios.get(endpoints.list, { withCredentials: true }),
                    axios.get(`${API}/admin/square-receipts/auto-credit-stats`, { withCredentials: true }),
                ]);
                setSettings(s.data);
                setSkewDraft(String(s.data?.time_skew_max_minutes ?? 10));
                setRows(q.data.rows || []);
                setStats(st.data);
            }
        } catch { /* silent */ }
        setLoading(false);
    }, [isCashier, endpoints.list]);
    useEffect(() => { fetchAll(); }, [fetchAll]);

    const toggle = async () => {
        if (!settings) return;
        setSavingToggle(true);
        try {
            const next = !settings.enabled;
            await axios.post(`${API}/admin/settings/auto-credit-receipts`, { enabled: next }, { withCredentials: true });
            setSettings({ ...settings, enabled: next });
            toast.success(`Auto-credit ${next ? 'enabled' : 'disabled'}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save toggle');
        } finally { setSavingToggle(false); }
    };

    const toggle247 = async () => {
        if (!settings) return;
        setSavingAlways(true);
        try {
            const next = !settings.always_on;
            await axios.post(`${API}/admin/settings/auto-credit-receipts`, { always_on: next }, { withCredentials: true });
            setSettings({ ...settings, always_on: next });
            toast.success(next
                ? '24/7 auto-credit ON — runs during business hours too'
                : 'Reverted to after-hours-only mode');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save 24/7 toggle');
        } finally { setSavingAlways(false); }
    };

    const saveSkew = async () => {
        if (!settings) return;
        const raw = String(skewDraft).trim();
        const n = parseInt(raw, 10);
        if (Number.isNaN(n) || n < 10 || n > 240) {
            toast.error('Enter a number between 10 and 240 minutes');
            setSkewDraft(String(settings.time_skew_max_minutes ?? 10));
            return;
        }
        if (n === settings.time_skew_max_minutes) return;
        setSavingSkew(true);
        try {
            const { data } = await axios.post(`${API}/admin/settings/auto-credit-receipts`,
                { time_skew_max_minutes: n }, { withCredentials: true });
            setSettings({ ...settings, time_skew_max_minutes: data.time_skew_max_minutes });
            setSkewDraft(String(data.time_skew_max_minutes));
            toast.success(`Freshness window set to ±${data.time_skew_max_minutes} min`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save threshold');
            setSkewDraft(String(settings.time_skew_max_minutes ?? 10));
        } finally { setSavingSkew(false); }
    };

    const auditOk = async (id, txn) => {
        setBusy(id);
        try {
            const payload = txn ? { txn_id_confirmation: txn } : {};
            await axios.post(endpoints.ok(id), payload, { withCredentials: true });
            toast.success('Audited OK');
            setTxnDraft((d) => { const n = { ...d }; delete n[id]; return n; });
            await fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        } finally { setBusy(null); }
    };

    const submitClawback = async () => {
        if (!clawbackOf || !reason.trim()) return;
        setBusy(clawbackOf.id);
        try {
            await axios.post(endpoints.claw(clawbackOf.id),
                { reason: reason.trim() }, { withCredentials: true });
            toast.success(`Clawed back ${fmtMoney(clawbackOf.credit_amount)} from ${clawbackOf.player_name}`);
            setClawbackOf(null);
            setReason('');
            await fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Clawback failed');
        } finally { setBusy(null); }
    };

    return (
        <div id="after-hours-auto-credit" className="card-dark p-4 space-y-3" data-testid="after-hours-auto-credit-card">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                    <h3 className="text-white text-base font-black flex items-center gap-2">
                        <Bot className="w-4 h-4 text-[#39FF14]" />
                        {isCashier ? 'Auto-loaded receipts' : (settings?.always_on ? '24/7 auto-credit' : 'After-hours auto-credit')}
                        {rows.length > 0 && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-amber-500/20 border border-amber-500/40 text-amber-300">
                                {rows.length} to audit
                            </span>
                        )}
                    </h3>
                    {isCashier ? (
                        <p className="text-[10px] text-[#A0AAB2] mt-0.5">
                            Receipts the AI auto-credited to players in your pool. Review and sign off (or claw back) each one.
                        </p>
                    ) : (
                    <p className="text-[10px] text-[#A0AAB2] mt-0.5 flex items-center gap-1.5 flex-wrap">
                        {settings?.always_on
                            ? <>Runs <span className="text-[#FFD700] font-bold">all hours</span> · HIGH-confidence OCR · ≤ {settings ? fmtMoney(settings.per_receipt_cap) : '—'} per receipt · ≤ {settings ? fmtMoney(settings.nightly_cap) : '—'} per player / 24h · receipt must be ≤</>
                            : <>Outside 8am–10pm CT · HIGH-confidence OCR · ≤ {settings ? fmtMoney(settings.per_receipt_cap) : '—'} per receipt · ≤ {settings ? fmtMoney(settings.nightly_cap) : '—'} per player per night · receipt must be ≤</>
                        }
                        <span className="inline-flex items-center gap-1">
                            <Input
                                type="number"
                                min={10}
                                max={240}
                                value={skewDraft}
                                onChange={(e) => setSkewDraft(e.target.value)}
                                onBlur={saveSkew}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                disabled={savingSkew}
                                className="w-12 h-5 px-1 py-0 text-[10px] font-mono font-bold bg-[#0a0a0a] border-[#FFD700]/40 text-[#FFD700] text-center"
                                data-testid="cfg-time-skew-min"
                                aria-label="Freshness window (minutes)"
                            />
                            <span>min old</span>
                            {savingSkew && <Loader2 className="w-2.5 h-2.5 animate-spin" />}
                        </span>
                    </p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {settings && (
                        <>
                            <button onClick={toggle} disabled={savingToggle}
                                className={`px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-wider border ${
                                    settings.enabled
                                        ? 'bg-[#39FF14]/15 border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14]/25'
                                        : 'bg-rose-500/15 border-rose-500/50 text-rose-300 hover:bg-rose-500/25'
                                }`}
                                title={settings.enabled ? 'Auto-credit is enabled — click to turn off' : 'Auto-credit is disabled — click to turn on'}
                                data-testid="auto-credit-toggle"
                            >
                                {savingToggle ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : (settings.enabled ? 'ON' : 'OFF')}
                            </button>
                            <button onClick={toggle247} disabled={savingAlways || !settings.enabled}
                                className={`px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-wider border transition-colors ${
                                    settings.always_on
                                        ? 'bg-[#FFD700]/20 border-[#FFD700]/60 text-[#FFD700] hover:bg-[#FFD700]/30'
                                        : 'bg-zinc-800 border-white/10 text-zinc-400 hover:bg-zinc-700'
                                } ${!settings.enabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                                title={
                                    !settings.enabled
                                        ? 'Turn auto-credit ON first'
                                        : settings.always_on
                                            ? 'Currently running 24/7 — click to revert to after-hours only'
                                            : 'Click to run auto-credit during business hours too (24/7 mode)'
                                }
                                data-testid="auto-credit-always-on-toggle"
                            >
                                {savingAlways ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : (settings.always_on ? '24/7' : 'AFTER-HRS')}
                            </button>
                        </>
                    )}
                    <button onClick={fetchAll} disabled={loading}
                        className="p-1.5 rounded-lg border border-white/10 hover:bg-white/5">
                        <RefreshCw className={`w-4 h-4 text-[#A0AAB2] ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Morning spike alert — fires once per day if last night > 2× avg */}
            {stats?.alert?.spike && (
                <div className="rounded-lg bg-amber-500/15 border border-amber-500/50 p-3 flex items-start gap-2.5 text-amber-100"
                    data-testid="auto-credit-spike-alert">
                    <ShieldAlert className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">
                            ⚠️ Unusual auto-credit volume · {stats.alert.ratio}× normal
                        </div>
                        <div className="text-xs mt-1 leading-snug">{stats.alert.message}</div>
                    </div>
                </div>
            )}

            {/* Stat strip + 7-day sparkline */}
            {stats && (
                <div className="rounded-lg bg-zinc-950 border border-white/10 p-3 flex items-center gap-4 flex-wrap" data-testid="auto-credit-stats">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Last night</div>
                        <div className="text-white font-black text-lg leading-tight font-mono">
                            {fmtMoney(stats.last_night?.amount || 0)}
                        </div>
                        <div className="text-[10px] text-zinc-500">{stats.last_night?.count || 0} receipt{stats.last_night?.count === 1 ? '' : 's'}</div>
                    </div>
                    <Sparkline series={stats.series || []} />
                </div>
            )}

            {loading && rows.length === 0 ? (
                <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-4 h-4 text-[#39FF14] animate-spin" />
                </div>
            ) : rows.length === 0 ? (
                <p className="text-center text-[#A0AAB2] py-4 text-sm">Nothing needs audit — overnight crediting is clean ✨</p>
            ) : (
                <div className="space-y-2">
                    {rows.map((r) => {
                        const src = r.image_b64 ? `data:${r.image_mime || 'image/png'};base64,${r.image_b64}` : (!isCashier ? `${API}/admin/square-receipts/${r.id}/image` : null);
                        const flags = r.suspicion_flags || [];
                        const isSuspicious = flags.length > 0;
                        const ocrTxn = (r.ocr_txn_id || '').trim();
                        const typedTxn = (txnDraft[r.id] || '').trim();
                        const txnMatches = ocrTxn && typedTxn && typedTxn.toLowerCase() === ocrTxn.toLowerCase();
                        // OK gate — clean rows: always enabled.
                        // Suspicious rows: only when the typed txn id matches.
                        // Suspicious + no OCR'd txn id: never OK-able (must clawback).
                        const canOk = !isSuspicious || (Boolean(ocrTxn) && txnMatches);
                        const flagLabels = {
                            duplicate_txn: 'Possible duplicate',
                            weak_ocr: 'Weak OCR read',
                        };
                        return (
                            <div key={r.id}
                                className={`rounded-xl bg-zinc-950 border p-3 space-y-2 ${
                                    isSuspicious ? 'border-rose-500/60' : 'border-amber-500/30'
                                }`}
                                data-testid={`audit-row-${r.id}`}
                            >
                                <div className="flex items-center gap-3">
                                    {src && <img src={src} alt="receipt" className="w-12 h-12 rounded object-cover border border-white/10" />}
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-bold text-white truncate">{r.player_name}</div>
                                        <div className="text-[10px] text-zinc-500">
                                            {fmtTime(r.created_at)} · {r.ocr_last4 ? `•••• ${r.ocr_last4}` : '—'} · {r.ocr_merchant || '—'}
                                        </div>
                                        {ocrTxn && (
                                            <div className="text-[10px] text-zinc-400 font-mono mt-0.5 truncate" title={`PayPal transaction ID: ${ocrTxn}`}>
                                                TXN: <span className="text-zinc-200">{ocrTxn}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="font-mono font-black text-[#39FF14] text-lg">{fmtMoney(r.credit_amount)}</div>
                                    <div className="flex flex-col sm:flex-row gap-1.5">
                                        <Button size="sm"
                                            onClick={() => auditOk(r.id, isSuspicious ? typedTxn : undefined)}
                                            disabled={busy === r.id || !canOk}
                                            title={
                                                canOk
                                                    ? 'Sign off — no balance change'
                                                    : ocrTxn
                                                        ? 'Type the PayPal transaction ID below to confirm before approving'
                                                        : 'No transaction ID on file — please Claw Back instead'
                                            }
                                            className="bg-emerald-500 text-zinc-900 hover:bg-emerald-400 font-black text-[11px] px-2.5 py-1 h-auto disabled:opacity-40 disabled:cursor-not-allowed"
                                            data-testid={`audit-ok-${r.id}`}>
                                            <ShieldCheck className="w-3.5 h-3.5 mr-1" /> OK
                                        </Button>
                                        <Button size="sm" onClick={() => { setClawbackOf(r); setReason(''); }} disabled={busy === r.id}
                                            className="bg-rose-500 text-white hover:bg-rose-400 font-black text-[11px] px-2.5 py-1 h-auto"
                                            data-testid={`audit-clawback-${r.id}`}>
                                            <ShieldAlert className="w-3.5 h-3.5 mr-1" /> Claw back
                                        </Button>
                                    </div>
                                </div>

                                {/* Suspicion gate — only shows for flagged rows.
                                    Admin must type/paste the PayPal txn id
                                    from the screenshot; OK stays disabled
                                    until it matches (case-insensitive). */}
                                {isSuspicious && (
                                    <div className="rounded-lg bg-rose-500/10 border border-rose-500/40 p-2 space-y-1.5"
                                        data-testid={`audit-suspicion-gate-${r.id}`}>
                                        <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-rose-300">
                                            <ShieldAlert className="w-3 h-3" />
                                            Flagged: {flags.map((f) => flagLabels[f] || f).join(' · ')}
                                        </div>
                                        <p className="text-[10px] text-rose-200/90 leading-snug">
                                            Open the screenshot and type the PayPal transaction ID exactly as shown. OK unlocks when it matches.
                                        </p>
                                        <div className="flex items-center gap-1.5">
                                            <Input
                                                value={txnDraft[r.id] || ''}
                                                onChange={(e) => setTxnDraft((d) => ({ ...d, [r.id]: e.target.value }))}
                                                placeholder={ocrTxn ? 'Type txn ID from receipt…' : 'No txn ID on file — Claw Back instead'}
                                                disabled={!ocrTxn || busy === r.id}
                                                className="bg-zinc-950 border-rose-500/40 text-zinc-100 text-xs h-8 font-mono"
                                                data-testid={`audit-txn-input-${r.id}`}
                                            />
                                            {typedTxn && (
                                                <span
                                                    className={`text-[10px] font-black shrink-0 ${
                                                        txnMatches ? 'text-emerald-300' : 'text-rose-300'
                                                    }`}
                                                    data-testid={`audit-txn-match-${r.id}`}
                                                >
                                                    {txnMatches ? '✓ MATCH' : '✗ NO MATCH'}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {clawbackOf && (
                <div className="rounded-xl bg-rose-500/10 border border-rose-500/40 p-3 space-y-2">
                    <div className="text-xs text-rose-200 font-bold">
                        Clawback {fmtMoney(clawbackOf.credit_amount)} from {clawbackOf.player_name}?
                    </div>
                    <Input value={reason} onChange={(e) => setReason(e.target.value)}
                        maxLength={200}
                        placeholder="Reason (player will see this)"
                        className="bg-zinc-950 border-zinc-700 text-zinc-100 text-sm"
                        data-testid="audit-clawback-reason"
                    />
                    <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => { setClawbackOf(null); setReason(''); }}>Cancel</Button>
                        <Button size="sm" disabled={!reason.trim() || busy === clawbackOf.id}
                            onClick={submitClawback}
                            className="bg-rose-500 text-white hover:bg-rose-400 font-black"
                            data-testid="audit-clawback-submit">
                            {busy === clawbackOf.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                            Confirm clawback
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AfterHoursAutoCreditCard;

/**
 * Tiny inline 7-day bar chart of nightly auto-credit totals. Today's bar is
 * highlighted; spikes vs. the 7-day average are tinted amber so anomalies
 * stand out at a glance.
 */
const Sparkline = ({ series }) => {
    if (!series || series.length === 0) return null;
    const max = Math.max(1, ...series.map((s) => s.amount));
    const avg = series.reduce((a, s) => a + s.amount, 0) / series.length;
    return (
        <div className="flex items-end gap-1 h-12 flex-1 min-w-[140px]" title="Last 7 nights">
            {series.map((s, i) => {
                const pct = (s.amount / max) * 100;
                const isToday = i === series.length - 1;
                const isSpike = s.amount > 0 && s.amount > avg * 1.8;
                const tone = isToday
                    ? 'bg-[#39FF14]'
                    : isSpike
                        ? 'bg-amber-400'
                        : 'bg-zinc-600';
                return (
                    <div key={s.date}
                        className="flex-1 flex flex-col items-center justify-end h-full group"
                        title={`${s.date}: ${fmtMoney(s.amount)} · ${s.count} rcpt`}>
                        <div
                            className={`w-full rounded-sm ${tone} transition-all`}
                            style={{ height: `${Math.max(2, pct)}%` }}
                        />
                    </div>
                );
            })}
        </div>
    );
};
